import * as React from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useT } from '@/components/visitone/visitone-provider'
import { AccessPills } from '@/components/visitone/access-status'
import { AreaNav } from '@/components/visitone/area-nav'
import { PageHeader } from '@/components/visitone/page-header'
import {
  ServerDataTable,
  type TableColumn,
  type TableRow,
  type TableState,
} from '@/components/visitone/server-data-table'
import { RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { DayNav } from '@/components/visitone/day-nav'
import { Button, buttonVariants } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Notice } from '@/components/ui/notice'
import { useCurrentTenantId } from '@/components/tenant/tenant-context'
import { useAccess } from '@/hooks/use-access'
import { useGuestList, useGuestSearch } from '@/hooks/use-guests'
import {
  GuestsRequestError,
  isForbidden,
  isNotFound,
  type GuestListId,
  type GuestListRow,
  type GuestSource,
  type GuestSummary,
  type GuestVenue,
} from '@/lib/bff/guests'
import {
  GUEST_LIST_FEATURE,
  isGuestListId,
  offeredGuestLists,
  openingList,
} from '@/lib/guests/guest-lists'
import { getLastUsedGuestList, setLastUsedGuestList } from '@/lib/guests/last-used-list'
import { daysBetween, formatLongDate, formatTenantZoneName, isIsoDate, shiftDays, tenantToday } from '@/lib/format/tenant-date'
import { formatInstant, guestName, membershipCell, messageTitle, nameCell, activityCell, visitTodayCell } from '@/lib/guests/guest-text'
import type { Translator } from '@/lib/i18n'
import { guestListPath, guestRecordPath } from '@/lib/routing'

/** What a search keeps in browser history state: the term and the list to go back to. Never in the URL. */
export type GuestsRouteState = { guestSearch?: { term: string; from?: GuestListId } }

const FIELD_ID = 'guest-find'
const MAX_TERM = 100

/** The field error for a `q` problem from the BFF, if the search was refused. */
function problemMessage(t: Translator, error: unknown): string | undefined {
  if (!(error instanceof GuestsRequestError) || error.status !== 400) return undefined
  const code = error.problems.find((problem) => problem.field === 'q')?.code
  if (code === 'too_short') return t('guests.find.tooShort')
  if (code === 'too_long') return t('guests.find.tooLong')
  if (code === 'contact_term_not_permitted') return t('guests.find.contactNotPermitted')
  return t('guests.find.invalid')
}

/*
 * Guests (VIS-460): find a guest in the current tenant, or open one of the
 * guest lists. Each list has its own URL (`/guests?list=…`); a search
 * replaces the list, and its term lives in browser history state only, so a
 * contact detail never reaches a URL. The BFF has already removed what the
 * caller may not see; the caller's actions only choose the help text.
 */
export function GuestsSurface() {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const tenantId = useCurrentTenantId()
  const { pageStatus, tenantName, can, hasFeature } = useAccess()
  const status = pageStatus('guests')
  const contact = can('guests.contact_details.view')
  // A Landlord's list or search is recorded before it is returned (VIS-330), so its failure says nothing was shown.
  const landlord = can('landlords.manage')

  const offered = React.useMemo(() => offeredGuestLists(hasFeature), [hasFeature])
  const remembered = getLastUsedGuestList(tenantId)
  const rawList = params.get('list')
  const namedList = rawList === 'arrivals-today' ? 'arrivals' : rawList

  let activeList: GuestListId | null = null
  let listRefused = false
  if (isGuestListId(namedList)) {
    const feature = GUEST_LIST_FEATURE[namedList]
    if (feature !== null && !hasFeature(feature)) listRefused = true
    else if (!offered.includes(namedList)) listRefused = true
    else activeList = namedList
  } else {
    activeList = openingList(offered, remembered)
  }

  React.useEffect(() => {
    if (activeList && tenantId) setLastUsedGuestList(tenantId, activeList)
  }, [activeList, tenantId])

  // A search keeps the list's URL, Arrivals' day and venue included, to go back to.
  const listPath = activeList
    ? guestListPath(
        activeList,
        activeList === 'arrivals' ? { date: params.get('date') ?? undefined, venue: params.get('venue') ?? undefined } : {},
      )
    : '/guests'
  const searched = (location.state as GuestsRouteState | null)?.guestSearch ?? null

  const [term, setTerm] = React.useState(searched?.term ?? '')
  const [clientError, setClientError] = React.useState<string>()
  const [edited, setEdited] = React.useState(false)
  const input = React.useRef<HTMLInputElement>(null)

  const search = useGuestSearch(searched?.term ?? null)
  const serverError = edited ? undefined : problemMessage(t, search.error)
  const fieldError = clientError ?? serverError

  React.useEffect(() => {
    if (serverError) input.current?.focus()
  }, [serverError])

  React.useEffect(() => {
    setTerm(searched?.term ?? '')
    setClientError(undefined)
    setEdited(false)
  }, [searched?.term])

  if (listRefused) return <RefusedSurface />
  if (searched && (isNotFound(search.error) || isForbidden(search.error))) return <RefusedSurface />

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = term.trim()
    if (trimmed.length < 2 || trimmed.length > MAX_TERM) {
      setClientError(t(trimmed.length < 2 ? 'guests.find.tooShort' : 'guests.find.tooLong'))
      input.current?.focus()
      return
    }
    setClientError(undefined)
    setEdited(false)
    if (trimmed === searched?.term) {
      void search.revalidate()
      return
    }
    navigate(searched ? `${location.pathname}${location.search}` : listPath, {
      state: { guestSearch: { term: trimmed, from: searched?.from ?? activeList ?? undefined } } satisfies GuestsRouteState,
    })
  }

  const showListNav = offered.length >= 2

  return (
    <div className="flex flex-col gap-5">
      <div>
        <PageHeader
          title={t('guests.title')}
          status={<AccessPills status={status} />}
          summary={
            status.summary === 'viewOnly'
              ? t('core.access.viewOnlyNote', { tenant: tenantName })
              : t('guests.summary', { tenant: tenantName })
          }
        />
        <form role="search" aria-label={t('guests.find.label')} onSubmit={submit} noValidate className="flex max-w-xl flex-wrap items-end gap-2">
          <Field
            id={FIELD_ID}
            label={t('guests.find.label')}
            help={t(contact ? 'guests.find.helpContact' : 'guests.find.help')}
            error={fieldError}
            className="min-w-0 flex-1 basis-56"
          >
            <Input
              ref={input}
              type="search"
              autoComplete="off"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value)
                setClientError(undefined)
                setEdited(true)
              }}
            />
          </Field>
          <Button type="submit" variant="primary">
            <Search />
            {t('guests.find.submit')}
          </Button>
        </form>
      </div>

      {showListNav ? (
        <AreaNav
          label={t('guests.lists.label')}
          items={offered.map((id) => ({
            to: guestListPath(id),
            label: t(`guests.lists.names.${id}`),
            current: !searched && id === activeList,
          }))}
        />
      ) : null}

      {searched ? (
        <SearchResults
          term={searched.term}
          contact={contact}
          tenant={tenantName}
          search={search}
          landlord={landlord}
          refused={search.error instanceof GuestsRequestError && search.error.status === 400}
          onClear={() => navigate(activeList ? `${location.pathname}${location.search}` : '/guests')}
        />
      ) : activeList ? (
        <GuestList key={activeList} list={activeList} landlord={landlord} tenant={tenantName} />
      ) : null}
    </div>
  )
}

function openAction(t: Translator) {
  return {
    label: t('guests.columns.actions'),
    render: (row: TableRow) => (
      <Link
        to={guestRecordPath(row.id)}
        aria-label={t('guests.row.openName', { name: row.label })}
        className={buttonVariants({ variant: 'secondary', size: 'sm' })}
      >
        {t('guests.row.open')}
      </Link>
    ),
  }
}

function UnavailableNotice({ unavailable, onRetry }: { unavailable: GuestSource[]; onRetry: () => void }) {
  const t = useT()
  if (!unavailable.includes('memberships')) return null
  return (
    <Notice tone="warning" title={t('guests.unavailable.membershipsTitle')}>
      {t('guests.unavailable.membershipsList')}{' '}
      <Button variant="link" size="sm" className="px-0 underline" onClick={onRetry}>
        {t('guests.unavailable.retry')}
      </Button>
    </Notice>
  )
}

function listColumns(t: Translator, list: GuestListId): TableColumn[] {
  const name = { key: 'name', label: t('guests.columns.name'), width: '14rem' }
  switch (list) {
    case 'arrivals':
      return [name, { key: 'visit', label: t('guests.columns.visit') }, { key: 'membership', label: t('guests.columns.membership') }]
    case 'failed-messages':
      return [name, { key: 'message', label: t('guests.columns.notDelivered') }, { key: 'lastTried', label: t('guests.columns.lastTried') }]
    default:
      return [name, { key: 'visit', label: t('guests.columns.latestVisit') }]
  }
}

function listRow(t: Translator, list: GuestListId, row: GuestListRow, timeZone: string | undefined): TableRow {
  const name = guestName(t, row.name)
  const cells: TableRow['cells'] = { name: nameCell(t, row.name, row.contact?.email) }
  if (list === 'arrivals') {
    cells.visit = visitTodayCell(t, row.arriving)
    cells.membership = membershipCell(t, row.currentMemberships)
  } else if (list === 'failed-messages') {
    cells.message = { primary: messageTitle(t, row.channel, row.kind) }
    cells.lastTried = { primary: timeZone ? formatInstant(row.lastTried, timeZone) : '' }
  } else {
    cells.visit = activityCell(t, row.latestActivity)
  }
  return { id: row.guestId, label: name, cells }
}

type TenantClock = { today: string; timeZone: string }

/*
 * The tenant's today (VIS-490): the BFF's `today`, moved on by the days the
 * tenant's clock has turned since it arrived, read again every minute. So
 * the "today" wording follows the tenant's midnight without reloading a
 * list, and a wrong browser clock doesn't move the day. Unknown until the
 * first response.
 */
function useTenantToday(clock: TenantClock | undefined): string | undefined {
  const [, tick] = React.useReducer((count: number) => count + 1, 0)
  React.useEffect(() => {
    const id = window.setInterval(tick, 60_000)
    return () => window.clearInterval(id)
  }, [])
  const anchor = React.useMemo(() => clock && { ...clock, local: tenantToday(clock.timeZone) }, [clock])
  if (!anchor) return undefined
  return shiftDays(anchor.today, daysBetween(anchor.local, tenantToday(anchor.timeZone)))
}

const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

function GuestList({ list, landlord, tenant }: { list: GuestListId; landlord: boolean; tenant: string }) {
  const t = useT()
  const [params, setParams] = useSearchParams()
  const arrivals = list === 'arrivals'

  // Arrivals' day and venue live in the URL (VIS-490): `date` is left out for today.
  const rawDate = arrivals ? params.get('date') : null
  const invalidDate = rawDate !== null && !isIsoDate(rawDate)
  const date = rawDate !== null && !invalidDate ? rawDate : undefined
  const venue = arrivals ? (params.get('venue') ?? 'all') : 'all'

  const view = `${date ?? 'today'}|${venue}`
  const [paging, setPaging] = React.useState({ view, page: 0 })
  // A page belongs to one day and venue. Changing either starts again at the
  // first page, including when that day or venue is opened again.
  if (paging.view !== view) setPaging({ view, page: 0 })
  const page = paging.view === view ? paging.page : 0

  // A link whose date isn't real asks for nothing, so nothing unrecorded is shown (VIS-330).
  const guests = useGuestList(invalidDate ? null : list, page, venue, date)
  const [venues, setVenues] = React.useState<GuestVenue[]>([{ id: 'all', name: '' }])
  const [clock, setClock] = React.useState<TenantClock>()
  const today = useTenantToday(clock)

  React.useEffect(() => {
    if (guests.page?.venues.length) setVenues(guests.page.venues)
  }, [guests.page?.venues])

  React.useEffect(() => {
    const served = guests.page
    if (!served?.today) return
    setClock((known) =>
      known?.today === served.today && known.timeZone === served.timeZone ? known : { today: served.today, timeZone: served.timeZone },
    )
  }, [guests.page])

  const shown = invalidDate ? undefined : (date ?? guests.page?.date ?? today)
  // Rows show only under the heading of the day they are for.
  const data = guests.page && (!arrivals || guests.page.date === shown) ? guests.page : undefined

  const update = (next: { date?: string | null; venue?: string }) => {
    setParams(
      (current) => {
        const query = new URLSearchParams(current)
        query.set('list', 'arrivals')
        if (next.date === null || (next.date !== undefined && next.date === today)) query.delete('date')
        else if (next.date !== undefined) query.set('date', next.date)
        if (next.venue === 'all') query.delete('venue')
        else if (next.venue !== undefined) query.set('venue', next.venue)
        return query
      },
      { replace: true },
    )
  }

  const unknownVenue = guests.error instanceof GuestsRequestError && guests.error.code === 'invalid_venue'
  React.useEffect(() => {
    if (unknownVenue) update({ venue: 'all' })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per refusal
  }, [unknownVenue])

  if (isNotFound(guests.error) || isForbidden(guests.error)) return <RefusedSurface />

  // Today asks again only when the tenant's day has turned while the list was open.
  const showToday = () => {
    if (rawDate !== null) update({ date: null })
    else if (shown !== today) void guests.revalidate()
  }
  const showDay = (day: string) => {
    if (day === today && rawDate === null) showToday()
    else update({ date: day })
  }

  const rows = (data?.data ?? []).map((row) => listRow(t, list, row, data?.timeZone))
  let state: TableState
  if (guests.error) state = 'error'
  else if (!data) state = 'loading'
  else if (rows.length === 0) state = 'empty'
  else state = 'data'

  const isToday = shown === undefined ? rawDate === null : shown === today
  const long = shown ? formatLongDate(shown) : undefined
  const venueName = venues.find((entry) => entry.id === venue)?.name
  const narrowed = venue !== 'all' && venueName ? venueName : undefined
  const pagination = data?.pagination
  const count = pagination?.totalCount ?? rows.length

  let name = t(`guests.lists.names.${list}`)
  let caption = name
  let announcement = ''
  let empty = {
    title: t(`guests.lists.empty.${list}.title`),
    body: t(`guests.lists.empty.${list}.body`),
  }
  if (arrivals) {
    if (invalidDate) name = t('guests.lists.arrivals.heading')
    else if (isToday) name = t('guests.lists.arrivals.headingToday')
    else name = t('guests.lists.arrivals.headingOn', { date: long ?? '' })
    caption = isToday && long ? t('guests.lists.arrivals.captionToday', { date: long }) : name

    if (state === 'loading' && !invalidDate) {
      announcement = isToday ? t('guests.lists.arrivals.loadingToday') : t('guests.lists.arrivals.loadingOn', { date: long ?? '' })
    } else if (state === 'data' || state === 'empty') {
      announcement = isToday
        ? t('guests.lists.arrivals.loadedToday', { count })
        : t('guests.lists.arrivals.loadedOn', { date: long ?? '', count })
    }

    if (isToday) {
      empty = {
        title: t('guests.lists.empty.arrivals.title'),
        body: narrowed
          ? t('guests.lists.empty.arrivals.bodyVenue', { venue: narrowed })
          : t('guests.lists.empty.arrivals.body'),
      }
    } else {
      const past = Boolean(shown && today && shown < today)
      const values = { date: long ?? '', venue: narrowed ?? '' }
      empty = {
        title: t('guests.lists.empty.arrivals.titleOn', values),
        body: past
          ? t(narrowed ? 'guests.lists.empty.arrivals.bodyPastVenue' : 'guests.lists.empty.arrivals.bodyPast', values)
          : t(narrowed ? 'guests.lists.empty.arrivals.bodyFutureVenue' : 'guests.lists.empty.arrivals.bodyFuture', values),
      }
    }
  }

  const zone = clock?.timeZone
  const zoneNote =
    zone && zone !== browserTimeZone()
      ? t('guests.lists.arrivals.zoneNote', { zone: formatTenantZoneName(zone), tenant })
      : undefined
  // A Landlord's list is recorded before it is returned (VIS-330); so is any caller's the BFF says it couldn't record.
  const notRecorded = landlord || (guests.error instanceof GuestsRequestError && guests.error.code === 'access_not_recorded')

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className={arrivals ? 'min-w-0 max-w-prose basis-full' : 'min-w-0 max-w-prose'}>
          <h2 className="text-lg font-semibold text-fg-strong [overflow-wrap:anywhere]">{name}</h2>
          {arrivals && isToday && long ? <p className="text-sm font-medium text-fg">{long}</p> : null}
          <p className="mt-1 text-sm text-fg-muted">{t(`guests.lists.definitions.${list}`)}</p>
        </div>
        {arrivals ? (
          <>
            <DayNav id="arrivals-date" value={shown} today={today} onChange={showDay} onToday={showToday} zoneNote={zoneNote} />
            <Field id="guest-venue" label={t('guests.lists.venue')} className="w-full sm:w-64">
              <NativeSelect value={venue} onChange={(event) => update({ venue: event.target.value })}>
                {venues.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.id === 'all' ? t('guests.lists.allVenues') : entry.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </>
        ) : null}
      </div>

      {arrivals ? (
        <p className="sr-only" role="status" aria-live="polite">
          {announcement}
        </p>
      ) : null}

      {data ? <UnavailableNotice unavailable={data.unavailable} onRetry={() => void guests.revalidate()} /> : null}

      {invalidDate ? (
        <Notice tone="warning" title={t('guests.lists.arrivals.invalidTitle')}>
          {t('guests.lists.arrivals.invalidBody')}{' '}
          <Button variant="link" size="sm" className="px-0 underline" onClick={showToday}>
            {t('guests.lists.arrivals.showToday')}
          </Button>
        </Notice>
      ) : (
        <ServerDataTable
          layout="stacked"
          state={state}
          onRetry={() => void guests.revalidate()}
          columns={listColumns(t, list)}
          rows={rows}
          rowActions={openAction(t)}
          caption={caption}
          regionLabel={caption}
          footer={state === 'loading' ? t('guests.lists.loading') : t('guests.lists.count', { count })}
          pagination={pagination ? { page: pagination.page, totalPages: pagination.totalPages, onPageChange: (next) => setPaging({ view, page: next }) } : undefined}
          loadingRowCount={5}
          messages={{
            loadErrorTitle: t(notRecorded ? 'guests.loadFailed.landlordList.title' : 'guests.loadFailed.title'),
            loadErrorBody: t(notRecorded ? 'guests.loadFailed.landlordList.body' : 'guests.loadFailed.body'),
            retry: t('guests.loadFailed.retry'),
            retryVariant: 'secondary',
          }}
          empty={empty}
        />
      )}
    </div>
  )
}

function searchRow(t: Translator, guest: GuestSummary): TableRow {
  return {
    id: guest.guestId,
    label: guestName(t, guest.name),
    cells: {
      name: nameCell(t, guest.name, guest.contact?.email),
      customerNumber: { primary: guest.customerNumber ?? t('guests.row.noCustomerNumber') },
      membership: membershipCell(t, guest.currentMemberships),
      activity: activityCell(t, guest.latestActivity),
    },
  }
}

function SearchResults({
  term,
  contact,
  tenant,
  search,
  landlord,
  refused,
  onClear,
}: {
  term: string
  contact: boolean
  tenant: string
  search: ReturnType<typeof useGuestSearch>
  landlord: boolean
  refused: boolean
  onClear: () => void
}) {
  const t = useT()
  const result = search.result
  const rows = (result?.data ?? []).map((guest) => searchRow(t, guest))

  let state: TableState
  if (search.error) state = 'error'
  else if (!result || search.isLoading) state = 'loading'
  else if (rows.length === 0) state = 'no-results'
  else state = 'data'

  let announcement = ''
  if (state === 'loading') announcement = t('guests.search.searching')
  else if (state === 'data' || state === 'no-results') announcement = t('guests.search.found', { count: rows.length })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-fg-strong">
          {t('guests.search.heading')}
        </h2>
        <Button variant="secondary" onClick={onClear}>
          {t('guests.search.clear')}
        </Button>
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {refused ? '' : announcement}
      </p>

      {refused ? null : (
        <>
          {result?.more ? (
            <Notice tone="info" title={t('guests.search.moreTitle')}>
              {t('guests.search.moreBody', { count: rows.length })}
            </Notice>
          ) : null}
          {result ? <UnavailableNotice unavailable={result.unavailable} onRetry={() => void search.revalidate()} /> : null}
          <ServerDataTable
            layout="stacked"
            state={state}
            onRetry={() => void search.revalidate()}
            columns={[
              { key: 'name', label: t('guests.columns.name'), width: '14rem' },
              { key: 'customerNumber', label: t('guests.columns.customerNumber') },
              { key: 'membership', label: t('guests.columns.membership') },
              { key: 'activity', label: t('guests.columns.latestActivity') },
            ]}
            rows={rows}
            rowActions={openAction(t)}
            caption={t('guests.search.caption')}
            regionLabel={t('guests.search.caption')}
            footer={state === 'loading' ? t('guests.search.searching') : t('guests.search.found', { count: rows.length })}
            loadingRowCount={3}
            messages={
              landlord
                ? {
                    loadErrorTitle: t('guests.loadFailed.landlordSearch.title'),
                    loadErrorBody: t('guests.loadFailed.landlordSearch.body'),
                    retry: t('guests.loadFailed.retry'),
                    retryVariant: 'secondary',
                  }
                : {
                    loadErrorTitle: t('guests.loadFailed.title'),
                    loadErrorBody: t('guests.loadFailed.body'),
                    retry: t('guests.loadFailed.retry'),
                  }
            }
            noResults={{
              title: t('guests.search.noMatchTitle'),
              body: t(contact ? 'guests.search.noMatchBodyContact' : 'guests.search.noMatchBody', { tenant, term }),
            }}
          />
        </>
      )}
    </div>
  )
}
